import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { articles, getArticle } from "@/mocks/articles";
import { usePageSeo } from "@/hooks/usePageSeo";
import Ticker from "./components/Ticker";
import HotRank from "./components/HotRank";
import FeedSection from "./components/FeedSection";
import SubscribeBox from "./components/SubscribeBox";
import { CategoryBadge } from "@/components/feature/ArticleCard";

const hero = getArticle("shehui-reverse-travel") ?? articles[0];
const secondary = [getArticle("yule-festival-red-carpet"), getArticle("wangluo-anonymous")].filter(
  Boolean
) as typeof articles;

const picks = articles.filter((a) => a.featured).concat(articles.filter((a) => !a.featured)).slice(0, 4);

const tags = [
  "反向旅游",
  "红毯造型",
  "momo",
  "弹幕文化",
  "慢综艺",
  "演唱会经济",
  "citywalk",
  "热播剧",
  "电子榨菜",
  "追剧",
  "娱乐八卦",
  "热点",
];

const steps = [
  { icon: "ri-close-circle-line", title: "免下载安装", desc: "无需安装任何工具与App，网页在线看，点开即用。" },
  { icon: "ri-cursor-line", title: "直接打开", desc: "浏览器一键直达，不用注册登录，访问即可浏览。" },
  { icon: "ri-refresh-line", title: "实时浏览", desc: "热点每日滚动刷新，随时跟上新鲜八卦与热讯。" },
  { icon: "ri-device-line", title: "多端适配", desc: "手机版电脑版自适应切换，在哪都能流畅吃瓜。" },
];

function categoryCover(slug: string): string {
  const hit = articles.find((a) => a.cat === slug);
  return hit ? hit.cover : hero.cover;
}

export default function Home() {
  usePageSeo({
    title: "吃瓜现场官网 - 吃瓜网页在线看免下载 | 实时热点八卦手机版电脑版浏览",
    description:
      "吃瓜现场聚合娱乐八卦、综艺现场与全网热讯，网页在线看、直接打开、免下载即可实时浏览，手机版电脑版自适应切换。今日热点、热榜排行一网打尽。",
    ld: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "吃瓜现场首页",
      inLanguage: "zh-CN",
      description: "聚合娱乐八卦、综艺现场与全网热讯的实时浏览资讯站。",
    },
  });

  return (
    <>
      <Ticker />

      {/* 头条 + 热榜 */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 pt-6 md:pt-8" aria-label="头条推荐">
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          {/* 头条大卡 */}
          <div className="lg:col-span-2 flex flex-col gap-5">
            <Link
              to={`/article/${hero.slug}`}
              className="group relative block overflow-hidden rounded-3xl"
              aria-label={hero.title}
            >
              <img
                src={hero.cover}
                alt={`${hero.title}头条封面`}
                title={`${hero.title} - 吃瓜现场`}
                className="h-[300px] w-full object-cover transition-transform duration-700 group-hover:scale-105 md:h-[420px]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/40 to-foreground-950/10" aria-hidden="true"></div>
              <div className="absolute inset-x-0 bottom-0 p-5 md:p-7">
                <div className="flex flex-wrap items-center gap-2">
                  <CategoryBadge cat={hero.cat} catName={hero.catName} dark />
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-background-50/15 px-2.5 py-1 text-xs font-medium text-background-50">
                    <i className="ri-fire-fill text-secondary-300" aria-hidden="true"></i> 头条热瓜
                  </span>
                </div>
                <h1 className="mt-3 max-w-2xl font-heading text-2xl font-black leading-snug text-background-50 md:text-4xl">
                  {hero.title}
                </h1>
                <p className="mt-3 hidden max-w-2xl text-sm leading-relaxed text-background-100/90 md:block">
                  {hero.excerpt}
                </p>
                <div className="mt-4 flex items-center gap-4 text-xs text-background-100/80">
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-user-3-line" aria-hidden="true"></i> {hero.source}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-eye-line" aria-hidden="true"></i> {hero.views.toLocaleString()} 人围观
                  </span>
                  <span className="ml-auto inline-flex items-center gap-1 text-background-50">
                    立即阅读 <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" aria-hidden="true"></i>
                  </span>
                </div>
              </div>
            </Link>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              {secondary.map((a) => (
                <Link
                  key={a.slug}
                  to={`/article/${a.slug}`}
                  className="group flex gap-4 overflow-hidden rounded-2xl border border-background-200/70 bg-background-100/50 p-3 transition-colors hover:border-primary-200"
                >
                  <img
                    src={a.cover}
                    alt={`${a.title}推荐配图`}
                    title={`${a.title} - 吃瓜现场`}
                    className="h-20 w-24 shrink-0 rounded-xl object-cover sm:h-24 sm:w-32"
                  />
                  <div className="flex min-w-0 flex-col justify-center">
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-primary-600">
                      <i className="ri-flashlight-line" aria-hidden="true"></i> 正在热读
                    </span>
                    <h3 className="mt-1.5 line-clamp-2 text-sm font-semibold leading-snug text-foreground-950 group-hover:text-primary-700 sm:text-base">
                      {a.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <HotRank />
        </div>
      </section>

      {/* 栏目速览 */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 pt-10 md:pt-14" aria-labelledby="cat-title">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-primary-500">频道一览</p>
            <h2 id="cat-title" className="mt-2 font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-3xl">
              想看哪个频道？随便挑
            </h2>
          </div>
        </div>
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {categories.map((c) => {
            const cover = categoryCover(c.slug);
            return (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                className="group relative block overflow-hidden rounded-2xl"
                aria-label={`进入${c.name}频道`}
              >
                <img
                  src={cover}
                  alt={`${c.name}频道封面`}
                  title={`${c.name} - 吃瓜现场`}
                  className="h-44 w-full object-cover transition-transform duration-500 group-hover:scale-105 md:h-48"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/25 to-transparent" aria-hidden="true"></div>
                <div className="absolute inset-x-0 bottom-0 p-4">
                  <p className="text-xs font-medium uppercase tracking-widest text-background-100/80">{c.en}</p>
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <h3 className="font-heading text-lg font-bold text-background-50">{c.name}</h3>
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-background-50/15 text-background-50 transition-colors group-hover:bg-primary-500">
                      <i className="ri-arrow-right-line text-sm" aria-hidden="true"></i>
                    </span>
                  </div>
                  <p className="mt-1 line-clamp-1 text-xs text-background-100/80">{c.tagline}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* 内容流 */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 pt-12 md:pt-16">
        <FeedSection />
      </section>

      {/* 平台价值 */}
      <section className="mt-12 bg-background-100/60 py-12 md:mt-16 md:py-16" aria-labelledby="step-title">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center">
            <p className="text-xs font-semibold uppercase tracking-widest text-primary-500">为什么值得用</p>
            <h2 id="step-title" className="mt-2 font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-3xl">
              免下载 · 在线看 · 随时随地开席
            </h2>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {steps.map((s) => (
              <div key={s.title} className="flex flex-col items-start rounded-2xl border border-background-200/70 bg-background-50 p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500 text-background-50">
                  <i className={`${s.icon} text-xl`} aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 编辑精选 */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 pt-12 md:pt-16" aria-labelledby="pick-title">
        <div className="flex items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-accent-600">Editor&apos;s Picks</p>
            <h2 id="pick-title" className="mt-2 font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-3xl">
              编辑精选 · 别错过这些
            </h2>
          </div>
          <Link to="/hot" className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-accent-700 hover:text-accent-600">
            去热点榜看看 <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {picks.map((a, i) => (
            <Link
              key={a.slug}
              to={`/article/${a.slug}`}
              className="group flex flex-col rounded-2xl border border-accent-100 bg-accent-50/60 p-3 transition-colors hover:border-accent-200"
            >
              <img
                src={a.cover}
                alt={`${a.title}精选封面`}
                title={`${a.title} - 吃瓜现场`}
                className="aspect-[16/10] w-full rounded-xl object-cover"
              />
              <div className="flex items-center gap-2 px-1 pt-3">
                <span className="font-heading text-lg font-black text-accent-600">0{i + 1}</span>
                <span className="text-xs text-foreground-600">{a.catName} · {a.date}</span>
              </div>
              <h3 className="mt-1.5 line-clamp-2 px-1 pb-2 font-heading text-sm font-bold leading-snug text-foreground-950 group-hover:text-accent-700">
                {a.title}
              </h3>
            </Link>
          ))}
        </div>
      </section>

      {/* 热搜话题 */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 pt-12 md:pt-16" aria-labelledby="tag-title">
        <div className="rounded-3xl bg-foreground-950 px-6 py-8 md:px-10 md:py-10">
          <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-secondary-300">
                <i className="ri-fire-fill text-base" aria-hidden="true"></i> 热搜关键词
              </p>
              <h2 id="tag-title" className="mt-2 font-heading text-2xl font-black text-background-50 md:text-3xl">
                大家都在聊什么
              </h2>
            </div>
            <Link to="/hot" className="inline-flex shrink-0 items-center gap-2 rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600">
              查看完整热点榜 <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-2.5">
            {tags.map((t, i) => (
              <Link
                key={t}
                to="/hot"
                className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm transition-transform hover:-translate-y-0.5 ${
                  i < 3
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-50/10 text-background-50 hover:bg-background-50/20"
                }`}
              >
                <span className="font-heading font-bold">{i + 1}</span> {t}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 订阅 */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 pt-12 md:pt-16 pb-6 md:pb-10">
        <SubscribeBox />
      </section>

      {/* 关于与问答引导 */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 pb-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Link
            to="/about"
            className="group flex flex-col justify-between rounded-2xl border border-background-200/70 bg-background-100/50 p-6 transition-colors hover:border-primary-300"
          >
            <div>
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent-100 text-accent-700">
                <i className="ri-building-2-line text-lg" aria-hidden="true"></i>
              </span>
              <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">认识吃瓜现场</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                一个专注热点娱乐八卦与全网热讯的实时浏览平台，坚持内容多样、更新及时、浏览轻松。
              </p>
            </div>
            <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary-700">
              了解更多 <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" aria-hidden="true"></i>
            </span>
          </Link>
          <Link
            to="/faq"
            className="group flex flex-col justify-between rounded-2xl border border-background-200/70 bg-background-100/50 p-6 transition-colors hover:border-accent-300"
          >
            <div>
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-secondary-100 text-secondary-800">
                <i className="ri-question-line text-lg" aria-hidden="true"></i>
              </span>
              <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">使用遇到问题？</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                关于免下载访问、手机电脑切换、内容更新与投稿等问题，我们整理了一份使用指南。
              </p>
            </div>
            <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-accent-700">
              查看常见问题 <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" aria-hidden="true"></i>
            </span>
          </Link>
        </div>
      </section>
    </>
  );
}